import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateStoryTags } from "../api/stories";

interface TagEditorProps {
	storyId: string;
	tags: string[];
	/** Query keys to invalidate after a successful update */
	invalidate?: unknown[][];
}

export function TagEditor({ storyId, tags, invalidate = [] }: TagEditorProps) {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [adding, setAdding] = useState(false);
	const [input, setInput] = useState("");
	const [saving, setSaving] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	async function save(nextTags: string[]) {
		setSaving(true);
		try {
			await updateStoryTags(storyId, nextTags);
			for (const key of invalidate) {
				queryClient.invalidateQueries({ queryKey: key });
			}
		} finally {
			setSaving(false);
		}
	}

	async function removeTag(tag: string) {
		await save(tags.filter((t) => t !== tag));
	}

	async function addTag() {
		const trimmed = input.trim().slice(0, 30);
		if (!trimmed || tags.includes(trimmed)) {
			setAdding(false);
			setInput("");
			return;
		}
		setAdding(false);
		setInput("");
		await save([...tags, trimmed]);
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			e.preventDefault();
			addTag();
		} else if (e.key === "Escape") {
			setAdding(false);
			setInput("");
		}
	}

	function startAdding(e: React.MouseEvent) {
		e.stopPropagation();
		setAdding(true);
		// focus after render
		setTimeout(() => inputRef.current?.focus(), 0);
	}

	return (
		<fieldset
			className="flex flex-wrap gap-1.5 items-center"
			aria-label={t("tags.ariaLabel")}
		>
			{tags.map((tag) => (
				<span
					key={tag}
					className="inline-flex items-center gap-1 font-ui text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-ink/20 text-ink/60 bg-white/40"
				>
					{tag}
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							removeTag(tag);
						}}
						disabled={saving}
						className="text-ink/30 hover:text-ink/70 transition-colors leading-none"
						aria-label={t("tags.removeTag", { tag })}
					>
						×
					</button>
				</span>
			))}

			{adding ? (
				<input
					ref={inputRef}
					type="text"
					value={input}
					onChange={(e) => setInput(e.target.value.slice(0, 30))}
					onKeyDown={handleKeyDown}
					onBlur={addTag}
					placeholder={t("tags.placeholder")}
					className="font-ui text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-ink/30 bg-white/60 outline-none w-24 text-ink/70 placeholder:text-ink/30"
				/>
			) : (
				<button
					type="button"
					onClick={startAdding}
					disabled={saving}
					className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors disabled:opacity-40"
				>
					{t("tags.addTag")}
				</button>
			)}
		</fieldset>
	);
}
